// One athlete in the Search results (Cards view) and the matching Table row.
import React from 'react';
import AddToListMenu from './AddToListMenu';
import VerifiedFlame from '../VerifiedFlame';
import { formatHeight } from '../../lib/playerSearchCatalog';

const initialsOf = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
const place = (p) => [p.high_school_city || p.city, p.high_school_state || p.state].filter(Boolean).join(', ');

// A search row carries no athlete-level "verified" field, only per-measurable `<stat>_verified` flags.
// The backend's verified_only filter means "at least one verified measurable", so the flame follows the
// same rule: an explicit is_verified wins, otherwise any true *_verified flag on the row.
export const isVerifiedRow = (p) => {
  if (p.is_verified != null) return !!p.is_verified;
  return Object.keys(p).some((k) => k.endsWith('_verified') && p[k] === true);
};

export function AthleteCard({ player, onOpen }) {
  const sub = [player.school ?? player.high_school, place(player)].filter(Boolean).join(' · ');
  return (
    <div className="srch-card" role="button" tabIndex={0} aria-label={`View ${player.name}'s profile`}
      onClick={() => onOpen(player.user_id)}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(player.user_id); } }}>
      <div className="srch-card__photo">
        {player.primary_photo
          ? <img src={player.primary_photo} alt="" className="srch-card__img" />
          : <span className="srch-card__initials" aria-hidden="true">{initialsOf(player.name)}</span>}
        {isVerifiedRow(player) && <span className="srch-card__flame"><VerifiedFlame size={16} /></span>}
      </div>
      <div className="srch-card__body">
        <div className="srch-card__row">
          <span className="srch-card__name">{player.name}</span>
          {player.power_score != null && <span className="srch-card__pwr" title={`Power Score ${player.power_score}`}>{player.power_score} PWR</span>}
        </div>
        <div className="srch-card__sub">{sub || 'N/A'}</div>
        <div className="srch-card__chips">
          {player.position_primary && <span className="adm-chip adm-chip--green">{player.position_primary}</span>}
          {player.graduation_year && <span className="adm-chip">{player.graduation_year}</span>}
          {player.aid_fit && <span className="adm-chip adm-chip--green" title="Your program's athletic aid is a strong financial fit for this athlete">Aid Fit</span>}
          <span className="srch-card__add"><AddToListMenu athlete={player} /></span>
        </div>
      </div>
    </div>
  );
}

const fmtDash = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(2));

export function AthleteTable({ players, onOpen, selected, onToggle }) {
  // 40YD only when some athlete on the page has a time: search is multi-sport, and a column of dashes
  // for a basketball or volleyball search is noise.
  const showDash = players.some((p) => fmtDash(p.dash_40) !== '—');
  return (
    <div className="adm__card adm__card--flush">
      <div className="adm-tablewrap">
        <table className="adm-table srch-table">
          <thead>
            <tr>
              <th className="srch-col--check"><span className="srch-sr">Compare</span></th>
              <th className="srch-col--athlete">ATHLETE</th><th>POS</th><th>CLASS</th><th>HT · WT</th>
              {showDash && <th className="adm-th--right">40YD</th>}<th className="adm-th--right">PWR</th>
              <th><span className="srch-sr">Add to list</span></th>
            </tr>
          </thead>
          <tbody>
            {players.map((p) => {
              const sub = [p.school ?? p.high_school, place(p)].filter(Boolean).join(' · ');
              const photo = p.primary_photo && p.primary_photo.startsWith('https://') ? p.primary_photo : null;
              return (
                <tr key={p.id} className="srch-row" onClick={() => onOpen(p.user_id)}>
                  <td className="srch-col--check" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(p.user_id)} onChange={() => onToggle(p.user_id)} aria-label={`Compare ${p.name}`} />
                  </td>
                  <td className="srch-col--athlete">
                    <div className="srch-who">
                      {photo
                        ? <img className="adm-avatar srch-who__img" src={photo} alt="" />
                        : <span className="adm-avatar" aria-hidden="true">{initialsOf(p.name)}</span>}
                      <div className="srch-who__text">
                        <div className="srch-who__name">
                          <button type="button" className="srch-rowname" onClick={(e) => { e.stopPropagation(); onOpen(p.user_id); }}>{p.name}</button>
                          {isVerifiedRow(p) && <VerifiedFlame size={14} />}
                        </div>
                        {sub && <div className="adm-cell__meta">{sub}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="srch-nowrap">{p.position_primary ? <span className="adm-chip adm-chip--green">{p.position_primary}</span> : ''}</td>
                  <td>{p.graduation_year || ''}</td>
                  <td className="srch-nowrap">{formatHeight(p.height_inches)}{p.weight_lbs ? ` · ${p.weight_lbs}` : ''}</td>
                  {showDash && <td className="adm-td--num">{fmtDash(p.dash_40)}</td>}
                  <td className="adm-td--num srch-pwr">{p.power_score ?? ''}</td>
                  <td className="srch-cell--end" onClick={(e) => e.stopPropagation()}><AddToListMenu athlete={p} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
